import type { FaqItem } from "@/lib/faq";

export type ComparisonValue = boolean | string;

export interface ComparisonRow {
	title: string;
	description: string;
	ploy: ComparisonValue;
	competitor: ComparisonValue;
}

export interface ComparisonCategory {
	category: string;
	rows: ComparisonRow[];
}

export interface Comparison {
	slug: string;
	competitor: string;
	competitorUrl: string;
	/** One-line description of the competitor used in structured data. */
	competitorDescription: string;
	/** Short positioning line shown under the competitor's name in the table header. */
	competitorLabel: string;
	competitorPrice: string;
	competitorPriceNote: string;
	/** Card copy for the /compare index. */
	summary: string;
	heading: string;
	description: string;
	badges: string[];
	metaTitle: string;
	metaDescription: string;
	/** Optional framing section shown between the hero and the table. */
	angle?: { title: string; paragraphs: string[] };
	categories: ComparisonCategory[];
	reasons: { title: string; body: string }[];
	fit: { ploy: string[]; competitor: string[] };
	faq: FaqItem[];
}

const platformServices = (competitor: {
	databases: ComparisonValue;
	queues: ComparisonValue;
	cron: ComparisonValue;
	workflows: ComparisonValue;
	sandboxes: ComparisonValue;
	ai: ComparisonValue;
	auth: ComparisonValue;
	storage: ComparisonValue;
}): ComparisonCategory => ({
	category: "Built-in platform services",
	rows: [
		{
			title: "Databases",
			description: "SQL storage attached to your project",
			ploy: "SQLite per project, migrations from git",
			competitor: competitor.databases,
		},
		{
			title: "Queues",
			description: "Background jobs and async messaging",
			ploy: "Included, retries built in",
			competitor: competitor.queues,
		},
		{
			title: "Cron and timers",
			description: "Run code on a schedule",
			ploy: "Included, down to the second",
			competitor: competitor.cron,
		},
		{
			title: "Durable workflows",
			description: "Multi-step jobs that survive failures",
			ploy: "Included, per-step retries",
			competitor: competitor.workflows,
		},
		{
			title: "State and file storage",
			description: "Key-value state, cache, and object storage",
			ploy: "Included",
			competitor: competitor.storage,
		},
		{
			title: "Sandboxes",
			description: "Isolated environments for untrusted code",
			ploy: "Included",
			competitor: competitor.sandboxes,
		},
		{
			title: "AI gateway",
			description: "Model access without provider keys",
			ploy: "Included, OpenAI-compatible",
			competitor: competitor.ai,
		},
		{
			title: "Authentication",
			description: "User sign-up, sign-in, and sessions",
			ploy: "Included, stored in your database",
			competitor: competitor.auth,
		},
	],
});

export const comparisons: Comparison[] = [
	{
		slug: "vercel",
		competitor: "Vercel",
		competitorUrl: "https://vercel.com",
		competitorDescription:
			"Proprietary hosted deployment platform for frontend and request/response applications.",
		competitorLabel: "Proprietary & hosted",
		competitorPrice: "From $0",
		competitorPriceNote: "Hobby plan with limits",
		summary:
			"The open serverless cloud against managed frontend hosting: what is included, what is an add-on, and what you can run yourself.",
		heading: "Ploy vs Vercel",
		description:
			"Ploy is an open-source serverless cloud with databases, queues, cron, durable workflows, sandboxes, and model access built in, and you can run all of it on your own infrastructure. Vercel is a proprietary hosting platform for request/response apps. Here is how they compare.",
		badges: [
			"Open source",
			"Self-hostable",
			"Batteries included",
			"No lock-in",
		],
		metaTitle: "Ploy vs Vercel — open serverless cloud vs managed hosting",
		metaDescription:
			"Compare Ploy and Vercel: Ploy is an open-source serverless cloud with databases, queues, cron, durable workflows, sandboxes, and model access included, self-hostable with no lock-in.",
		categories: [
			{
				category: "Deployment & infrastructure",
				rows: [
					{
						title: "Self-hosting option",
						description: "Deploy on your own infrastructure",
						ploy: "Run the full platform yourself",
						competitor: false,
					},
					{
						title: "Open source",
						description: "Full access to source code",
						ploy: "AGPLv3",
						competitor: false,
					},
					{
						title: "Vendor lock-in",
						description: "Freedom to migrate",
						ploy: "Same code runs self-hosted",
						competitor: "Platform dependent",
					},
					{
						title: "Runtime",
						description: "Where your code executes",
						ploy: "Isolate-based serverless runtime",
						competitor: "Vercel Functions and Edge Runtime",
					},
					{
						title: "Preview deployments",
						description: "A URL for every branch",
						ploy: true,
						competitor: true,
					},
					{
						title: "Git sources",
						description: "Where your code can live",
						ploy: "GitHub, or Ploy Git with no GitHub account",
						competitor: "GitHub, GitLab, Bitbucket",
					},
				],
			},
			{
				category: "Pricing",
				rows: [
					{
						title: "Free self-hosting",
						description: "Host unlimited projects for free",
						ploy: "Forever free",
						competitor: false,
					},
					{
						title: "Free managed tier",
						description: "What the free plan includes",
						ploy: "3 repositories, unlimited previews",
						competitor: "Hobby, non-commercial use",
					},
					{
						title: "Paid plan",
						description: "First paid tier",
						ploy: "Pro at $10/month per organization",
						competitor: "Pro at $20/month per member",
					},
					{
						title: "Build minutes",
						description: "Free tier build time",
						ploy: "Unlimited when self-hosted",
						competitor: "6,000 min/month (Hobby)",
					},
					{
						title: "Bandwidth",
						description: "Free tier data transfer",
						ploy: "Unlimited when self-hosted",
						competitor: "100 GB/month (Hobby)",
					},
				],
			},
			{
				category: "Developer experience",
				rows: [
					{
						title: "Frameworks",
						description: "First-class framework support",
						ploy: "Next.js, Vite, TanStack Start, static sites, workers",
						competitor: "Next.js and most frontend frameworks",
					},
					{
						title: "Custom domains",
						description: "Use your own domain names",
						ploy: "Pro plan",
						competitor: true,
					},
					{
						title: "Environment variables",
						description: "Manage secrets and configuration",
						ploy: true,
						competitor: true,
					},
					{
						title: "Skew protection",
						description: "Clients stay on the deployment that served them",
						ploy: "On by default",
						competitor: "Pro and Enterprise",
					},
					{
						title: "Local development",
						description: "Run the platform's services on your machine",
						ploy: "ploy dev emulates databases, queues, cron, and workflows",
						competitor: "vercel dev",
					},
					{
						title: "API access",
						description: "Programmatic deployment management",
						ploy: "REST API and CLI",
						competitor: "REST API and CLI",
					},
				],
			},
			platformServices({
				databases: "Marketplace add-ons",
				queues: "Vercel Queues (beta)",
				cron: "Cron jobs",
				workflows: "Vercel Workflow",
				storage: "Vercel Blob, marketplace KV",
				sandboxes: "Vercel Sandbox",
				ai: "AI Gateway",
				auth: "Marketplace add-ons",
			}),
		],
		reasons: [
			{
				title: "Everything included",
				body: "Databases, queues, cron, workflows, storage, sandboxes, auth, and AI are part of the platform, not a marketplace.",
			},
			{
				title: "Fully open source",
				body: "The whole platform is AGPLv3, so you can read how every primitive works.",
			},
			{
				title: "Self-host on your terms",
				body: "Run the same code on bare metal or any cloud with no licence fee.",
			},
			{
				title: "No lock-in",
				body: "Move between the managed cloud and your own infrastructure without a rewrite.",
			},
		],
		fit: {
			ploy: [
				"You want a database, queue, cron, and auth without stitching together add-ons.",
				"You are building agents or background-heavy apps, not only request/response pages.",
				"Data residency or procurement means the platform has to run inside your own infrastructure.",
				"You want to deploy without a GitHub account, from a laptop, CI, or an agent.",
			],
			competitor: [
				"You are all-in on Next.js and want the vendor that builds it to host it.",
				"You need Vercel-specific features such as its image optimization or its enterprise network.",
				"Your team already runs its workflow around Vercel's integrations and marketplace.",
			],
		},
		faq: [
			{
				question: "Is Ploy a drop-in replacement for Vercel?",
				answer:
					"For Next.js, Vite, and static projects, moving is usually a matter of connecting the repository and adding a short ploy.yaml. Vercel-specific APIs such as its KV or Blob SDKs map onto Ploy's state and file storage bindings, which you declare in ploy.yaml instead of provisioning in a marketplace.",
			},
			{
				question: "Does Ploy have preview deployments like Vercel?",
				answer:
					"Yes. Every push to a non-production branch gets its own deployment URL, on both GitHub-connected projects and Ploy Git repositories. Preview deployments are unlimited on the free plan.",
			},
			{
				question: "Can I self-host Ploy the way I cannot self-host Vercel?",
				answer:
					"Yes. Ploy is open source under AGPLv3 and the entire platform, including the dashboard, builder, runtime, databases, and queues, runs with Docker on your own infrastructure. There is no licence fee and no usage limit when self-hosting.",
			},
		],
	},
	{
		slug: "netlify",
		competitor: "Netlify",
		competitorUrl: "https://www.netlify.com",
		competitorDescription:
			"Proprietary hosted platform for static sites, frontends, and serverless functions.",
		competitorLabel: "Proprietary & hosted",
		competitorPrice: "From $0",
		competitorPriceNote: "Free plan with limits",
		summary:
			"Both deploy from git with previews. The difference is what sits behind the frontend: a full backend with databases, queues, and workflows, or functions plus add-ons.",
		heading: "Ploy vs Netlify",
		description:
			"Netlify made git-based deploys and deploy previews the default for frontends. Ploy keeps that workflow and adds the backend: serverless workers with SQLite databases, queues, cron, durable workflows, sandboxes, auth, and model access, all open source and self-hostable.",
		badges: ["Open source", "Self-hostable", "Full backend", "No lock-in"],
		metaTitle: "Ploy vs Netlify — open serverless cloud vs frontend hosting",
		metaDescription:
			"Compare Ploy and Netlify: both deploy from git with previews, but Ploy includes serverless workers, SQLite databases, queues, cron, durable workflows, sandboxes, and auth, and runs on your own infrastructure.",
		categories: [
			{
				category: "Deployment & infrastructure",
				rows: [
					{
						title: "Self-hosting option",
						description: "Deploy on your own infrastructure",
						ploy: "Run the full platform yourself",
						competitor: false,
					},
					{
						title: "Open source",
						description: "Full access to source code",
						ploy: "AGPLv3",
						competitor: false,
					},
					{
						title: "Preview deployments",
						description: "A URL for every branch",
						ploy: true,
						competitor: "Deploy Previews",
					},
					{
						title: "Git sources",
						description: "Where your code can live",
						ploy: "GitHub, or Ploy Git with no GitHub account",
						competitor: "GitHub, GitLab, Bitbucket, Azure DevOps",
					},
					{
						title: "Serverless compute",
						description: "Where backend code runs",
						ploy: "Isolate-based workers, scale to zero",
						competitor: "Netlify Functions and Edge Functions",
					},
					{
						title: "Long-running work",
						description: "Jobs that outlive a request",
						ploy: "Queues and durable workflows",
						competitor: "Background Functions (Pro)",
					},
				],
			},
			{
				category: "Pricing",
				rows: [
					{
						title: "Free self-hosting",
						description: "Host unlimited projects for free",
						ploy: "Forever free",
						competitor: false,
					},
					{
						title: "Free managed tier",
						description: "What the free plan includes",
						ploy: "3 repositories, unlimited previews",
						competitor: "Free plan with monthly limits",
					},
					{
						title: "Paid plan",
						description: "First paid tier",
						ploy: "Pro at $10/month per organization",
						competitor: "Pro, billed per member",
					},
					{
						title: "Build minutes",
						description: "Free tier build time",
						ploy: "Unlimited when self-hosted",
						competitor: "300 min/month (Free)",
					},
					{
						title: "Bandwidth",
						description: "Free tier data transfer",
						ploy: "Unlimited when self-hosted",
						competitor: "100 GB/month (Free)",
					},
				],
			},
			{
				category: "Developer experience",
				rows: [
					{
						title: "Frameworks",
						description: "First-class framework support",
						ploy: "Next.js, Vite, TanStack Start, static sites, workers",
						competitor: "Most frontend frameworks",
					},
					{
						title: "Custom domains",
						description: "Use your own domain names",
						ploy: "Pro plan",
						competitor: true,
					},
					{
						title: "Environment variables",
						description: "Manage secrets and configuration",
						ploy: true,
						competitor: true,
					},
					{
						title: "Monorepos",
						description: "Several projects from one repository",
						ploy: "Every ploy.yaml is a project",
						competitor: true,
					},
					{
						title: "Local development",
						description: "Run the platform's services on your machine",
						ploy: "ploy dev emulates databases, queues, cron, and workflows",
						competitor: "netlify dev",
					},
					{
						title: "Config as code",
						description: "Declare the project in the repository",
						ploy: "ploy.yaml",
						competitor: "netlify.toml",
					},
				],
			},
			platformServices({
				databases: "Netlify DB (beta, Postgres)",
				queues: "Bring your own",
				cron: "Scheduled Functions",
				workflows: "Bring your own",
				storage: "Netlify Blobs",
				sandboxes: false,
				ai: "AI Gateway",
				auth: "Via extensions",
			}),
		],
		reasons: [
			{
				title: "The backend is included",
				body: "Databases, queues, cron, workflows, storage, and auth are declared in ploy.yaml, not bolted on afterwards.",
			},
			{
				title: "Same workflow, more room",
				body: "Git push, previews per branch, rollbacks, and environment variables work the way you expect.",
			},
			{
				title: "Open source and self-hostable",
				body: "Run the whole platform on your own infrastructure under AGPLv3 with no usage limits.",
			},
			{
				title: "Built for agents too",
				body: "Durable workflows, sandboxes, and an AI gateway are there when an app grows beyond request/response.",
			},
		],
		fit: {
			ploy: [
				"Your app needs a database, background jobs, or scheduled work and you do not want to run them elsewhere.",
				"You want one platform for the frontend and the backend, including workers that scale to zero.",
				"You need to self-host, or want the option to later without changing your code.",
				"You are shipping AI features or agents and want sandboxes and model access built in.",
			],
			competitor: [
				"You are deploying a static site or Jamstack frontend and the backend lives somewhere else.",
				"Your team relies on Netlify-specific features such as its forms, split testing, or extensions.",
				"You deploy from GitLab, Bitbucket, or Azure DevOps and do not want to add a remote.",
			],
		},
		faq: [
			{
				question: "Can I move a Netlify site to Ploy?",
				answer:
					"Static sites and Vite apps move by connecting the repository and adding a ploy.yaml with the build command and output directory. Netlify Functions become a worker next to the frontend, and scheduled functions become cron triggers in ploy.yaml.",
			},
			{
				question: "Does Ploy have something like Netlify Blobs?",
				answer:
					"Yes. File storage gives every project an S3-style object store with put, get, list, and delete, and state gives you a durable key-value store with atomic JSON updates. Both are declared in ploy.yaml and appear on env in your worker.",
			},
			{
				question: "What about background jobs?",
				answer:
					"Ploy includes queues with automatic retries and durable workflows whose steps are persisted as they complete. On Netlify, long-running work means Background Functions on a paid plan or an external job system.",
			},
		],
	},
	{
		slug: "lovable",
		competitor: "Lovable",
		competitorUrl: "https://lovable.dev",
		competitorDescription:
			"AI app builder that generates full-stack web applications from natural language prompts.",
		competitorLabel: "AI app builder",
		competitorPrice: "From $0",
		competitorPriceNote: "Credit-based plans",
		summary:
			"Lovable builds the app; Ploy runs it. How an AI-generated app gets a real backend, its own database, and infrastructure you control.",
		heading: "Ploy vs Lovable",
		description:
			"Lovable generates an app from a prompt. Ploy is where that app runs in production: a serverless backend with its own SQLite database, queues, cron, auth, and sandboxes, deployed from git, on infrastructure you can own. They are different tools, and they work well together.",
		badges: ["Open source", "Self-hostable", "Real backend", "Your own data"],
		metaTitle: "Ploy vs Lovable — build the app, then run it on your own cloud",
		metaDescription:
			"Lovable generates your app; Ploy runs it. Compare an AI app builder with an open-source serverless cloud that gives generated apps a real backend, a SQLite database, queues, auth, and self-hosting.",
		angle: {
			title: "Different jobs, one workflow",
			paragraphs: [
				"Lovable is an AI builder: you describe an app and it writes the React frontend, wires up a backend, and publishes a preview. Ploy is a serverless cloud: it takes code from a git repository, builds it, and runs it with databases, queues, workflows, cron, auth, and model access attached.",
				"Lovable projects sync to GitHub. Connect that repository to Ploy, or push it to Ploy Git, and every change Lovable makes deploys to a preview URL, while production runs on a platform you can inspect, extend, and self-host. The generated frontend keeps working; the backend gets a real runtime.",
			],
		},
		categories: [
			{
				category: "What each one does",
				rows: [
					{
						title: "Generates your app",
						description: "Write the code from a prompt",
						ploy: "Bring your own code, from any tool",
						competitor: "Yes, chat-driven",
					},
					{
						title: "Runs your app",
						description: "Production hosting",
						ploy: "Serverless runtime, scales to zero",
						competitor: "Lovable hosting",
					},
					{
						title: "Deploys from git",
						description: "Push to deploy with previews",
						ploy: "GitHub or Ploy Git, preview per branch",
						competitor: "GitHub sync",
					},
					{
						title: "Custom domains",
						description: "Use your own domain names",
						ploy: "Pro plan",
						competitor: "Paid plans",
					},
					{
						title: "AI coding support",
						description: "Help for assistants building on the platform",
						ploy: "Agent skills for Claude Code, Cursor, and others",
						competitor: "Built-in agent",
					},
				],
			},
			{
				category: "Backend",
				rows: [
					{
						title: "Database",
						description: "Where application data lives",
						ploy: "SQLite per project, migrations from git",
						competitor: "Supabase (Postgres)",
					},
					{
						title: "Server-side code",
						description: "APIs, webhooks, and jobs",
						ploy: "Workers and full-stack frameworks",
						competitor: "Supabase Edge Functions",
					},
					{
						title: "Queues and workflows",
						description: "Background work that survives failures",
						ploy: "Included",
						competitor: "Not built in",
					},
					{
						title: "Cron",
						description: "Run code on a schedule",
						ploy: "Included",
						competitor: "Not built in",
					},
					{
						title: "Authentication",
						description: "User sign-up, sign-in, and sessions",
						ploy: "Included, stored in your database",
						competitor: "Supabase Auth",
					},
					{
						title: "Sandboxes",
						description: "Run generated code in isolation",
						ploy: "Included",
						competitor: false,
					},
					{
						title: "AI gateway",
						description: "Model access from your own backend",
						ploy: "Included, OpenAI-compatible",
						competitor: "Via integrations",
					},
				],
			},
			{
				category: "Ownership",
				rows: [
					{
						title: "Open source",
						description: "Full access to the platform's source",
						ploy: "AGPLv3",
						competitor: false,
					},
					{
						title: "Self-hosting",
						description: "Run the platform on your own infrastructure",
						ploy: "Run the full platform yourself",
						competitor: false,
					},
					{
						title: "Code ownership",
						description: "Your repository, your history",
						ploy: "Your git repository",
						competitor: "Exportable to GitHub",
					},
					{
						title: "Pricing model",
						description: "How you pay",
						ploy: "Free tier, Pro at $10/month, self-host free",
						competitor: "Credits per message",
					},
				],
			},
		],
		reasons: [
			{
				title: "A real backend for generated apps",
				body: "Give a Lovable app its own database, queues, cron, and auth without adding a second vendor for each.",
			},
			{
				title: "Keep using your builder",
				body: "Sync to GitHub, connect the repository, and every change deploys to a preview URL.",
			},
			{
				title: "Own the result",
				body: "The platform is open source and self-hostable, so the app you shipped is never trapped in a tool.",
			},
			{
				title: "Grows with the app",
				body: "Durable workflows, sandboxes, and model access are there when a prototype turns into a product.",
			},
		],
		fit: {
			ploy: [
				"You have an app, generated or hand-written, and need to run it with a database and background jobs.",
				"You want the backend on infrastructure you control, with the option to self-host.",
				"You are building agents and need sandboxes, workflows, and model access alongside the app.",
				"You want to deploy from git, with previews and rollbacks, rather than from a builder's publish button.",
			],
			competitor: [
				"You want to go from an idea to a working prototype without writing code.",
				"You are iterating on the UI by chatting and do not need a custom backend yet.",
				"Supabase already hosts your data and you are happy with its hosting.",
			],
		},
		faq: [
			{
				question: "Can I deploy a Lovable app on Ploy?",
				answer:
					"Yes. Lovable projects sync to a GitHub repository. Connect that repository to Ploy, add a ploy.yaml with the Vite build command and output directory, and every push deploys. Keep editing in Lovable; each change lands on a preview URL.",
			},
			{
				question: "Does Ploy generate apps like Lovable?",
				answer:
					"No. Ploy runs apps; it does not write them. It does ship agent skills for Claude Code, Cursor, and other assistants so an AI tool can scaffold a Ploy project with databases, queues, and workflows correctly, and an Agent SDK for building your own agents on the platform.",
			},
			{
				question: "What happens to the Supabase backend?",
				answer:
					"You can keep it and deploy only the frontend to Ploy, or move server-side logic into a worker with a Ploy SQLite database, auth, queues, and cron so the whole app lives on one platform. Which one makes sense depends on how much backend the app has.",
			},
		],
	},
];

export const comparisonBySlug: Record<string, Comparison> = Object.fromEntries(
	comparisons.map((comparison) => [comparison.slug, comparison]),
);
